import { Collection } from 'firestorter';
import BaseDocument from '../../Model/BaseDocument';
import Collections from '../../utils/collections';

/**
 * Enum for coach history event types.
 */
const HistoryEventType = {
  WHITE_LISTED: 'WHITE_LISTED',
  ACCOUNT_CREATED: 'ACCOUNT_CREATED',
  PROFILE_SET: 'PROFILE_SET',
  STRIPE_CONNECTED: 'STRIPE_CONNECTED',
  STRIPE_DEAUTHORIZED: 'DEAUTHORIZE_STRIPE_ACCOUNT',
  STRIPE_DEAUTHORIZATION_CLEANUP: 'STRIPE_ACCOUNT_DEAUTHORIZATION_CLEANUP',
  LEAD_FORM_CREATED: 'LEAD_FORM_CREATED',
  POST_PAYMENT_FORM_LINKED: 'POST_PAYMENT_FORM_LINKED',
  FIRST_PRODUCT_CREATED: 'FIRST_PRODUCT_CREATED',
  LOGO_SUBMITTED: 'LOGO_SUBMITTED',
  LOGO_APPROVED: 'LOGO_APPROVED',
  WEBSITE_SUBMITTED: 'WEBSITE_SUBMITTED',
  WEBSITE_APPROVED: 'WEBSITE_APPROVED',
  WELCOME_PACK_SUBMITTED: 'WELCOME_PACK_SUBMITTED',
  APP_LOGO_SET: 'APP_LOGO_SET',
  APP_THEME_SET: 'APP_THEME_SET',
  SLACK_CHANNEL_SET: 'SLACK_CHANNEL_SET',
  COACH_LAUNCHED: 'COACH_LAUNCHED',
  FIRST_LEAD: 'FIRST_LEAD',
  FIRST_SUBSCRIPTION: 'FIRST_SUBSCRIPTION',
  OFFBOARDED: 'OFFBOARDED',
  POST_PAYMENT_FORM_DELETED: 'POST_PAYMENT_FORM_DELETED',
  COACH_WEBSITE_DELETED: 'COACH_WEBSITE_DELETED',
  APP_ICON_DELETED: 'APP_ICON_DELETED',
  ZOHO_EMAIL_REMOVED: 'ZOHO_EMAIL_REMOVED',
  LEAD_FORM_ARCHIVED: 'LEAD_FORM_ARCHIVED',
  COACH_OFFBOARDING_NOTE_ADDED: 'COACH_OFFBOARDING_NOTE_ADDED',
  COACH_OFFBOARDING_NOTE_EDITED: 'COACH_OFFBOARDING_NOTE_EDITED',
  SLACK_CHANNEL_ARCHIVED: 'SLACK_CHANNEL_ARCHIVED',
  COACH_POD_ENABLED: 'COACH_POD_ENABLED',
  COACH_POD_REBALANCED: 'COACH_POD_REBALANCED',
  COACH_POD_CREATED: 'COACH_POD_CREATED',
  COACH_POD_DELETED: 'COACH_POD_DELETED',
  COACH_POD_ASSISTANT_ADDED: 'COACH_POD_ASSISTANT_ADDED',
  COACH_POD_ASSISTANT_REMOVED: 'COACH_POD_ASSISTANT_REMOVED',
  SOCIAL_HANDLES_ADDED: 'SOCIAL_HANDLES_ADDED',
  IS_AC_QUESTIONNAIRE_SUBMITTED: 'IS_AC_QUESTIONNAIRE_SUBMITTED',
  CONTENT_URLS_SUBMITTED: 'CONTENT_URLS_SUBMITTED',
  GOOGLE_CALENDAR_CREATED: 'GOOGLE_CALENDAR_CREATED',
  COACH_ONBOARDING_FORM_COMPLETED: 'COACH_ONBOARDING_FORM_COMPLETED',
  FIRST_CHAT_MESSAGE_SENT: 'FIRST_CHAT_MESSAGE_SENT',
  TRANSFORMATION_POST_SUBMITTED: 'TRANSFORMATION_POST_SUBMITTED',
  LAUNCH_POST_SUBMITTED: 'LAUNCH_POST_SUBMITTED',
  LAUNCH_DATE_EDITED: 'LAUNCH_DATE_EDITED',
  AC_FLAG_UPDATED: 'AC_FLAG_UPDATED',
  IS_FLAG_UPDATED: 'IS_FLAG_UPDATED',
  FEES_CONFIG_UPDATED: 'FEES_CONFIG_UPDATED',
  CAM_ASSIGNMENT_UPDATED: 'CAM_ASSIGNMENT_UPDATED',
};

/**
 * List of events need to be completed by CAM that is concerned with a coach's onboarding process .
 */
const CoachOnboardingEventListbyCAM = [
  HistoryEventType.ACCOUNT_CREATED,
  HistoryEventType.LEAD_FORM_CREATED,
  HistoryEventType.POST_PAYMENT_FORM_LINKED,
  HistoryEventType.FIRST_PRODUCT_CREATED,
  HistoryEventType.WEBSITE_APPROVED,
  HistoryEventType.APP_LOGO_SET,
  HistoryEventType.APP_THEME_SET,
  HistoryEventType.SLACK_CHANNEL_SET,
  HistoryEventType.COACH_LAUNCHED,
  HistoryEventType.FIRST_LEAD,
  HistoryEventType.FIRST_SUBSCRIPTION,
];

/**
 * List of events need to be completed by coach that is concerned with a coach's onboarding process .
 */
const OnboardingChecklistbyCoach = [
  HistoryEventType.PROFILE_SET,
  HistoryEventType.STRIPE_CONNECTED,
  HistoryEventType.COACH_ONBOARDING_FORM_COMPLETED,
  HistoryEventType.SOCIAL_HANDLES_ADDED,
  HistoryEventType.IS_AC_QUESTIONNAIRE_SUBMITTED,
  HistoryEventType.CONTENT_URLS_SUBMITTED,
  HistoryEventType.GOOGLE_CALENDAR_CREATED,
  HistoryEventType.FIRST_CHAT_MESSAGE_SENT,
];

/**
 * The "first chat message" step joined the checklist on this date. Coaches whose account is older already talk to
 * their account manager in the chat, so the step is only asked of accounts created from this date on.
 */
const FIRST_CHAT_MESSAGE_STEP_START = new Date('2026-11-01T00:00:00.000Z');

/**
 * The steps of the new-coach checklist that apply to a coach, given when their account was created.
 * The new-coach cutoff itself (who gets this checklist at all) is decided by the callers.
 * @param {Date} [accountCreatedAt] - When the coach's account was created (their ACCOUNT_CREATED event)
 * @returns {string[]} The checklist's event types, without the first chat message step for older accounts
 */
const getOnboardingChecklistByCoach = (accountCreatedAt) => (
  accountCreatedAt >= FIRST_CHAT_MESSAGE_STEP_START
    ? OnboardingChecklistbyCoach
    : OnboardingChecklistbyCoach.filter((type) => type !== HistoryEventType.FIRST_CHAT_MESSAGE_SENT)
);

/**
 * List of events need to be completed by coach that is concerned with a coach's content URLs submission.
 */
const ContentURLsChecklistbyCoach = [
  HistoryEventType.TRANSFORMATION_POST_SUBMITTED,
  HistoryEventType.LAUNCH_POST_SUBMITTED,
];

/**
 * List of events that require manual action to record.
 */
const ManualCoachEventList = [
  HistoryEventType.WEBSITE_APPROVED,
  HistoryEventType.COACH_LAUNCHED,
];

/**
 * Class representing a coach's history.
 * Stores documents tracking important milestone events of a coach.
 *
 * @class CoachHistory
 * @extends BaseDocument
 */
class CoachHistory extends BaseDocument {
  constructor(id, opts) {
    super(`${Collections.COACH_HISTORY}/${id}`, opts);
  }

  /**
   * Gets the coach ID associated with the history item.
   * @return {string}
   */
  get coachId() {
    return this.data.coachId;
  }

  /**
   * Gets the creation timestamp of the history item.
   * @return {Date}
   */
  get createdAt() {
    return this.data.createdAt?.toDate();
  }

  /**
   * Gets the ID of the user who actioned this history item.
   * @return {string}
   */
  get actionedBy() {
    return this.data.actionedBy;
  }

  /**
   * Gets the username of the user who actioned this history item.
   * @return {string}
   */
  get actionedByUserName() {
    return this.data.actionedByUserName;
  }

  /**
   * Gets the type of the history event.
   * The value corresponds to {@link HistoryEventType}.
   * @return {string}
   */
  get type() {
    return this.data.type;
  }

  /**
   * Gets the metadata of the history event.
   * @return {object}
   */
  get metadata() {
    return this.data.metadata;
  }

  /**
   * Retrieves the history of a specific coach.
   *
   * @param {string} coachId - The ID of the coach.
   * @returns {Promise<Collection>} A promise resolving to the coach's history collection.
   */
  static async getCoachHistoryById(coachId) {
    const coachHistoryCollection = new Collection(Collections.COACH_HISTORY, {
      createDocument: ({ id }, opts) => new CoachHistory(id, opts),
      query: (ref) => ref
        .where('coachId', '==', coachId)
        .orderBy('createdAt', 'desc'),
    });
    await coachHistoryCollection.fetch();
    return coachHistoryCollection;
  }

  /**
   * Retrieves the history doc for a coach lead by onboarding doc id.
   *
   * @param {string} onboardingDocId - The ID of the onboarding doc.
   * @returns {Promise<Collection>} A promise resolving to the history document.
   */
  static async getCoachHistoryByOnboardingDocId(onboardingDocId) {
    const historyDoc = new CoachHistory(onboardingDocId);
    await historyDoc.init();
    return historyDoc.exists ? historyDoc : null;
  }

  /**
   * Retrieves a specific history document by coach ID and history type.
   *
   * @param {string} coachId - The ID of the coach.
   * @param {string} historyType - The type of the history event.
   * @returns {Promise<CoachHistory|null>} A promise resolving to the history document or null if not found.
   */
  static async getCoachHistoryByCoachIdAndType(coachId, historyType) {
    const coachHistoryCollection = new Collection(Collections.COACH_HISTORY, {
      createDocument: ({ id }, opts) => new CoachHistory(id, opts),
      query: (ref) => ref
        .where('coachId', '==', coachId)
        .where('type', '==', historyType)
        .orderBy('createdAt', 'desc')
        .limit(1),
    });

    await coachHistoryCollection.fetch();

    return coachHistoryCollection.docs.length > 0 ? coachHistoryCollection.docs[0] : null;
  }

  /**
   * Adds a new history item for a coach.
   *
   * @param {object} params - Parameters for the new history item.
   * @param {string} params.type - The type of the history event.
   * @param {string} params.coachId - The ID of the coach.
   * @param {string} params.actionedBy - The ID of the user who took action on the item.
   * @param {string} params.actionedByUserName - The username of the acted user.
   * @param {Date} [params.createdAt=new Date()] - The creation timestamp.
   * @returns {Promise<void>}
   */
  static async addCoachHistoryItem({
    type,
    coachId,
    actionedBy,
    actionedByUserName,
    metadata,
  }) {
    const item = {
      type,
      coachId,
      actionedBy,
      actionedByUserName,
      createdAt: new Date(),
      ...(metadata && { metadata }),
    };

    const coachHistoryCollection = new Collection(Collections.COACH_HISTORY);
    await coachHistoryCollection.add(item);
  }

  /**
   * Marks a history event as completed.
   *
   * @param {string} coachId - The ID of the coach.
   * @param {string} loggedInUserId - The ID of the user who took action on the item.
   * @param {string} loggedInUserName - The username of the acted user.
   * @param {string} historyEventType - The type of the history event.
   * @returns {Promise<void>}
   */
  static async markAsCompleted(
    coachId,
    loggedInUserId,
    loggedInUserName,
    historyEventType,
  ) {
    return CoachHistory.addCoachHistoryItem({
      type: historyEventType,
      coachId,
      actionedBy: loggedInUserId,
      actionedByUserName: loggedInUserName,
    });
  }

  /**
   * Retrieves onboarding checklist events collection.
   *
   * @param {string[]} [coachIDs] - Optional array of coach IDs to filter by. If not provided, returns all.
   * @returns {Promise<Collection>} Collection of onboarding checklist events
   */
  static async getHistoryDocsByCoachIds(coachIDs = null) {
    const coachChecklistCollection = new Collection(Collections.COACH_HISTORY, {
      createDocument: ({ id }, opts) => new CoachHistory(id, opts),
      query: (ref) => {
        let query = ref;
        if (coachIDs && coachIDs.length > 0) {
          query = query.where('coachId', 'in', coachIDs);
        }
        return query;
      },
    });

    await coachChecklistCollection.fetch();

    return coachChecklistCollection;
  }
}

export default CoachHistory;
export {
  HistoryEventType,
  CoachOnboardingEventListbyCAM,
  OnboardingChecklistbyCoach,
  FIRST_CHAT_MESSAGE_STEP_START,
  getOnboardingChecklistByCoach,
  ContentURLsChecklistbyCoach,
  ManualCoachEventList,
};
